import type { LanguageDto } from "@core/data/dto/language.dto";
import { LanguageDtoSchema } from "@core/data/dto/language.dto";
import type { CurrentLanguageDto } from "@core/data/dto/update-current-language.dto";
import { CurrentLanguageDtoSchema } from "@core/data/dto/update-current-language.dto";
import { LanguageIsoCodeEnumMapper } from "@core/data/mappers/language-iso-code-enum.mapper";
import type {
    Language,
    LanguageInputProps,
    LanguageIsoCode,
    LanguageIsoCodeInputProps,
} from "@core/domain/models/language.model";
import {
    LanguageIsoCodeSchema,
    LanguageSchema,
} from "@core/domain/models/language.model";
import type { Nullable } from "@core/domain/types/nullable.type";
import { inject, injectable } from "inversify";

@injectable()
export class LanguageMapper {
    constructor(
        @inject(LanguageIsoCodeEnumMapper)
        private readonly languageIsoCodeEnumMapper: LanguageIsoCodeEnumMapper,
    ) {}

    map(languageDto: LanguageDto): Nullable<Language> {
        const languageDtoParsed = LanguageDtoSchema.safeParse(languageDto);

        if (!languageDtoParsed.success) {
            console.error(languageDtoParsed.error.issues);
            return null;
        }

        const maybeLanguage = LanguageSchema.safeParse({
            isoCode: this.languageIsoCodeEnumMapper.map(
                languageDtoParsed.data.key,
            ),
            label: languageDtoParsed.data.value,
        } satisfies LanguageInputProps);

        return maybeLanguage.success ? maybeLanguage.data : null;
    }

    mapToIsoCode(languageDto: CurrentLanguageDto): Nullable<LanguageIsoCode> {
        const languageDtoParsed =
            CurrentLanguageDtoSchema.safeParse(languageDto);

        if (!languageDtoParsed.success) {
            console.error(languageDtoParsed.error.issues);
            return null;
        }

        const maybeLanguage = LanguageIsoCodeSchema.safeParse({
            isoCode: this.languageIsoCodeEnumMapper.map(
                languageDtoParsed.data.locale,
            ),
        } satisfies LanguageIsoCodeInputProps);

        return maybeLanguage.success ? maybeLanguage.data : null;
    }
}
